// @flow
import React from 'react';
import { connect } from 'react-redux';
import {
    Box, Button, Image,
} from 'grommet';
import {
    defineMessages, type IntlShape, injectIntl,
} from 'react-intl';
import { withRouter } from 'speed-js-react';
import styled, { withTheme, ThemeContext } from 'styled-components';
import permissionRequired from 'esm-web-pro/decorators/permissionRequired';
import coverage from 'esm-web-pro/assets/images/sidebar/coverage.svg';
import administration from 'esm-web-pro/assets/images/sidebar/administration.svg';
import network from 'esm-web-pro/assets/images/sidebar/network.svg';
import projects from 'esm-web-pro/assets/images/sidebar/projects.svg';
import { getUserFullName } from 'speed-js-react/src/redux/auth/selector';
import { CONTEXT_NAVBAR_HEIGHT } from 'esm-web-pro/styles/theme';
import { BSS_URL_BASENAME_PREFIX } from 'esm-web-pro/routes/util';

const SidebarImage = styled(Image)`
  height: ${CONTEXT_NAVBAR_HEIGHT};
  width: ${CONTEXT_NAVBAR_HEIGHT};;
`;

const mapStateToProps = (state) => ({
    fullName: getUserFullName(state),
});

const ButtonBox = (props) => <Box {...props} />;
ButtonBox.defaultProps = {
    pad: { horizontal: 'medium', bottom: 'small' },
    align: 'center',
};

const SidebarButton = (props) => <Button {...props} />;

const StyledSidebarButton = styled(SidebarButton)`
  display: flex;
  justify-content: center;

  &:hover {
    background-color: ${(props) => props.theme.global.colors.navigation}
  }
`;
const PermissionsRequiredButton = permissionRequired()(StyledSidebarButton);

type Props = {
    history: Object,
    location: Object,
    theme: Object,
    intl: IntlShape,
    fullName: ?string,
}

const messages = defineMessages({
    'sidebar-coverages-title': {
        id: 'sidebar-coverages-title',
        defaultMessage: 'Coverage',
    },
    'sidebar-networks-title': {
        id: 'sidebar-networks-title',
        defaultMessage: 'Infrastructure',
    },
    'sidebar-administration-title': {
        id: 'sidebar-administration-title',
        defaultMessage: 'Administration',
    },
    'sidebar-projects-title': {
        id: 'sidebar-projects-title',
        defaultMessage: 'Projects',
    },
});


const sections = [
    {
        title: messages['sidebar-coverages-title'],
        link: '/coverage',
        permissions: ['addresses.add_coverage'],
        icon: coverage,
        paths: ['/coverage'],
    },
    {
        title: messages['sidebar-networks-title'],
        link: '/infrastructure/map',
        permissions: ['networks.view_gispoint', 'networks.view_gisline'],
        icon: network,
        paths: ['/infrastructure/map'],
    },
    {
        title: messages['sidebar-projects-title'],
        link: '/projects',
        permissions: ['project.view_projects'],
        icon: projects,
        paths: ['/projects'],
    },
];

@connect(mapStateToProps)
@withRouter
class SidebarNav extends React.Component<Props, {open: boolean}> {
    static defaultProps = {
        fullName: '',
    };

    handleOpen = (link: string) => {
        const { history } = this.props;
        history.push(link);
    };

    render() {
        const {
            location, intl: { formatMessage }, fullName, theme,
        } = this.props;

        const sidebarTheme = { global: { active: { background: theme.global.colors.navigation } } };

        const activeAdministration = location.pathname.startsWith('/administration');
        const brandGrey = theme.global.colors['brand-dark-grey'];
        return (
            <ThemeContext.Extend value={sidebarTheme}>
                <Box
                    direction="column"
                    height={`calc(100% - ${CONTEXT_NAVBAR_HEIGHT})`}
                    background={{ color: '#ffffff' }}
                >
                    <Box direction="column" overflow="auto" flex height="100px">
                        {sections.map((section) => {
                            const active = section.paths.some((url) => location.pathname.startsWith(url));
                            return (
                                <PermissionsRequiredButton
                                    key={section.title.id}
                                    active={active}
                                    href={`${BSS_URL_BASENAME_PREFIX}${section.link}`}
                                    onClick={(e) => {
                                        if (!e.metaKey && !e.ctrlKey) {
                                            e.preventDefault();
                                            this.handleOpen(section.link);
                                        }
                                    }}
                                    permissions={section.permissions}
                                    data-tip={formatMessage(section.title)}
                                    data-for="permission-required"
                                >
                                    <Box pad={{ horizontal: 'xxxsmall' }}>
                                        <SidebarImage alignSelf="center" src={section.icon} />
                                    </Box>
                                </PermissionsRequiredButton>
                            );
                        })}
                        {fullName && (
                            <Box flex alignSelf="end" justify="end" width="100%">
                                <PermissionsRequiredButton
                                    active={activeAdministration}
                                    onClick={(e) => {
                                        if (!e.metaKey && !e.ctrlKey) {
                                            e.preventDefault();
                                            this.handleOpen('/administration');
                                        }
                                    }}
                                    href="/administration"
                                    data-tip={formatMessage(messages['sidebar-administration-title'])}
                                    data-for="permission-required"
                                    permissions={['authorisation.view_administration']}
                                >
                                    <SidebarImage alignSelf="center" src={administration} />
                                </PermissionsRequiredButton>
                            </Box>
                        )}
                    </Box>
                </Box>
            </ThemeContext.Extend>
        );
    }
}

export default withTheme(injectIntl(SidebarNav));
